import * as React from 'react'
import ChatManager, { ChatData } from '../../model/ChatManager'
import Model, { AnnotationData, InputData, SearchResultsData, TransactionData, UserData } from '../../model/Model'

import { SettingsData } from '../../model/Settings'
import Annotation from '../Annotation/Annotation'
import Input from '../Input/Input'
import Search from '../Search/Search'
import SearchResults from '../Search/SearchResults'
import Transaction from '../Transaction/Transaction'
import Analysis from '../Analysis/Analysis'
import './Editor.css'
import { EnvironmentType, QuestionType, Metadata } from '@jibo-cloud/qas-client'

const queryString = require('query-string')

export interface EditorProps {
  appModel: Model;
  userData: UserData;
}

export interface EditorState {
  inputData: InputData;
  transactionData: TransactionData;
  annotationData: AnnotationData;
  searchResultsData: SearchResultsData;
  inputPanelMode: 'input' | 'search' | 'results' | 'analysis';
  statusMessage: string;
  searchResultsActiveTransactionId: string;
  intentIdFilter: string;
  sessionIdFilter: string;
  settings: SettingsData;
  chatData: ChatData;
  metadata: Metadata[];
  urlProperties: any;
}

export default class Editor extends React.Component<EditorProps, EditorState> {
  constructor (props: EditorProps) {
    super(props)
    // console.log(`Editor: constructor:`, props);
    this.state = {
      inputData: this.props.appModel.inputData,
      transactionData: {
        type: QuestionType.Qa,
        environment: EnvironmentType.Production,
        sessionId: '',
        id: '',
        category: '',
        intentId: '',
        intentDetail: '',
        input: '',
        response: {}
      },
      annotationData: this.props.appModel.resetAnnotationData(),
      searchResultsData: { database: 'na', result: [] },
      inputPanelMode: 'input',
      statusMessage: 'OK',
      searchResultsActiveTransactionId: '',
      intentIdFilter: '',
      sessionIdFilter: '',
      settings: this.props.appModel.settingsData,
      chatData: this.props.appModel.chatData,
      metadata: [],
      urlProperties: {}
    }
  }

  updateStatus (message: string, append = false) {
    let newMessage: string = message
    if (append) {
      newMessage = this.state.statusMessage + '; ' + message
    }
    this.setState({
      statusMessage: newMessage
    })
  }

  updateMetadata = () => {
    this.props.appModel
      .makeMetadataRequest()
      .then((metadata: Metadata[]) => {
        this.setState({
          metadata: metadata,
          inputData: this.props.appModel.inputData
        })
      })
      .catch((error: any) => {
        console.log(error)
        this.updateStatus(error)
      })
  };

  componentDidMount () {
    this.updateMetadata()
    const parsedUrlHash = queryString.parse(window.location.hash)
    const urlProperties: any = {
      command: parsedUrlHash.cmd || '',
      appName: parsedUrlHash.appName || '',
      database: parsedUrlHash.db || '',
      fieldName: parsedUrlHash.field || '',
      fieldValue: parsedUrlHash.value || ''
    }
    // console.log(urlProperties);
    if (urlProperties && urlProperties.command === 'search') {
      if (urlProperties.appName && urlProperties.database && urlProperties.fieldName && urlProperties.fieldValue) {
        const searchData = {
          appName: urlProperties.appName,
          database: urlProperties.database
        }
        searchData[urlProperties.fieldName] = urlProperties.fieldValue
        this.setState(
          {
            urlProperties: {}
          },
          () => {
            this.onClick('submitSearch', searchData)
          }
        )
      }
    }
  }

  submitAnnotation (data: AnnotationData) {
    this.updateStatus('submitAnnotation: annotation submitted.')
    this.props.appModel
      .submitAnnotationData(data)
      .then((annotationData: AnnotationData) => {
        // console.log(annotationData);
        if (annotationData) {
          this.setState({
            annotationData: annotationData
          })
          this.updateStatus('submitAnnotation: annotation response received.')
        } else {
          this.updateStatus('submitAnnotation: received invalid annotation data in response.')
        }
      })
      .catch((error: any) => {
        console.log(error)
        this.updateStatus(error)
      })
  }

  // generateChatMessageAnnotation(chatMessageData: ChatMessageData) {
  //     const chatMessageAnnotationData: AnnotationData = this.props.appModel.getEmptyAnnotationData('', chatMessageData.id);
  //     if (chatMessageData.type === 'canonicalQuestion') {
  //         chatMessageAnnotationData.issueType = IssueType.WrongAnswer;
  //     } else if (chatMessageData.type === 'answer') {
  //         chatMessageAnnotationData.issueType = IssueType.InadequateAnswer;
  //     }
  //     this.submitAnnotation(chatMessageAnnotationData);
  // }

  onClick = (action: string, data: any) => {
    switch (action) {
      case 'inputPanelMode':
        if (data === 'search' && this.state.inputPanelMode === 'search') {
          // this.setState({
          // });
        } else if (data === 'search') {
          this.setState({
            inputPanelMode: 'search'
          })
        } else if (data === 'results') {
          this.setState({
            inputPanelMode: 'results'
          })
        } else if (data === 'input') {
          this.setState({
            inputPanelMode: 'input'
          })
        } else if (data === 'analysis') {
          this.setState({
            inputPanelMode: 'analysis'
          })
        }
        break
      case 'submitInput': // Input or ChatPanel
        // console.log('submitInput', data);
        if (data.question) {
          this.updateStatus(`Question submitted: ${data.question}`)
          this.props.appModel
            .submitInputData(data)
            .then((transactionData: TransactionData) => {
              if (transactionData && transactionData.id && transactionData.serviceType && transactionData.appName) {
                this.setState({
                  inputData: this.props.appModel.inputData,
                  transactionData: transactionData,
                  annotationData: this.props.appModel.annotationData,
                  chatData: this.props.appModel.chatData
                })
                // console.log(transactionData);
                this.updateStatus('Transaction received.')
              } else {
                this.updateStatus('Invalid transaction data received.')
                console.log('Invalid transaction data received:', transactionData)
              }
            })
            .catch((error: any) => {
              console.log(error)
              this.updateStatus(error)
            })
        }
        break
      case 'submitAnnotation':
        // console.log('submitAnnotation', data);
        this.submitAnnotation(data)
        break
      case 'submitSearch':
        // console.log('submitSearch', data);
        this.props.appModel
          .submitSearchData(data)
          .then((searchResults: SearchResultsData) => {
            // console.log(searchResults);
            this.setState({
              searchResultsData: searchResults,
              searchResultsActiveTransactionId: '',
              intentIdFilter: '',
              sessionIdFilter: '',
              inputPanelMode: 'results'
            })
          })
          .catch((error: any) => {
            console.log(error)
            this.updateStatus(error)
          })
        break
      // case 'chatBubbleFlagged':
      //     // console.log('chatBubbleFlagged', data);
      //     this.generateChatMessageAnnotation(data);
      //     break;
      case 'chatBubbleSelected':
      // console.log('chatBubbleSelected', data);
      // fall through here to 'searchLink' is OK...
      case 'searchResultSelected':
        // console.log(action, data);
        if (Model.isTransactionData(data) || (ChatManager.isChatMessageData(data) && data.id)) {
          let transactionData: TransactionData | undefined
          let annotationData: AnnotationData | undefined
          this.props.appModel
            .submitSearchData({ database: 'transactions', transactionId: data.id })
            .then((searchResults: SearchResultsData) => {
              // console.log(searchResults);
              if (searchResults && searchResults.result && searchResults.result.length === 1) {
                transactionData = searchResults.result[0] as any
                if (transactionData) {
                  this.setState({
                    transactionData: transactionData,
                    searchResultsActiveTransactionId: transactionData.id!
                  })

                  // look up the associated annotation if it exists
                  this.props.appModel
                    .submitSearchData({ database: 'annotations', transactionId: transactionData.id })
                    .then((searchResults: SearchResultsData) => {
                      // console.log(searchResults);
                      if (searchResults && searchResults.result && searchResults.result.length === 1) {
                        annotationData = searchResults.result[0] as any
                      } else {
                        // new annotation data for transaction
                        annotationData = this.props.appModel.resetAnnotationData(
                          transactionData!.sessionId,
                          transactionData!.id,
                          transactionData!.intentId,
                          transactionData!.appName
                        )
                      }
                      this.setState({
                        annotationData: annotationData!
                      })
                    })
                    .catch((error: any) => {
                      console.log(error)
                      this.updateStatus(error)
                    })
                }
              }
            })
            .catch((error: any) => {
              console.log(error)
              this.updateStatus(error)
            })
        } else if (Model.isAnnotationData(data)) {
          let transactionData: TransactionData | undefined
          let annotationData: AnnotationData | undefined
          this.props.appModel
            .submitSearchData({ database: 'annotations', annotationId: data.id })
            .then((searchResults: SearchResultsData) => {
              // console.log(searchResults);
              if (searchResults && searchResults.result && searchResults.result.length === 1) {
                annotationData = searchResults.result[0] as any
                this.setState({
                  annotationData: annotationData!
                })
                // look up the associated transaction if it exists
                this.props.appModel
                  .submitSearchData({ database: 'transactions', transactionId: annotationData!.transactionId })
                  .then((searchResults: SearchResultsData) => {
                    // console.log(searchResults);
                    if (searchResults && searchResults.result && searchResults.result.length === 1) {
                      transactionData = searchResults.result[0] as any
                      this.setState({
                        transactionData: transactionData!,
                        searchResultsActiveTransactionId: transactionData!.id!
                      })
                    } else {
                      this.updateStatus('no transaction data.')
                    }
                  })
                  .catch((error: any) => {
                    console.log(error)
                    this.updateStatus(error)
                  })
              } else {
                this.updateStatus('no annotation data.')
              }
            })
            .catch((error: any) => {
              console.log(error)
              this.updateStatus(error)
            })
        }
        break
      case 'clearSessionId':
        this.updateStatus('SessionId cleared.')
        this.props.appModel.clearSessionId()
        this.setState({
          inputData: this.props.appModel.inputData
        })
        break
    }
  };

  onChanged = (action: string, data: any) => {
    // console.log(`onChanged:`, action, data);
    switch (action) {
      case 'updateAppName':
        this.props.appModel.inputAppName = data
        this.setState({
          inputData: this.props.appModel.inputData
        })
        break
      case 'updateIntentIdFilter':
        this.setState({
          intentIdFilter: data
        })
        break
      case 'updateSessionIdFilter':
        this.setState({
          sessionIdFilter: data
        })
        break
    }
  };

  render () {
    let inputSearchContents: any
    switch (this.state.inputPanelMode) {
      case 'input':
        inputSearchContents = (
          <Input
            data={this.state.inputData}
            appModel={this.props.appModel}
            clicked={this.onClick}
            changed={this.onChanged}
            chatData={this.props.appModel.chatData}
            metadata={this.state.metadata}
          />
        )
        break
      case 'search':
        inputSearchContents = (
          <div>
            <Search
              appModel={this.props.appModel}
              appName={this.props.appModel.inputAppName}
              clicked={this.onClick}
              changed={this.onChanged}
              userData={this.props.userData}
            />
          </div>
        )
        break
      case 'results':
        inputSearchContents = (
          <div>
            <SearchResults
              data={this.state.searchResultsData}
              activeTransactionId={this.state.searchResultsActiveTransactionId}
              intentIdFilter={this.state.intentIdFilter}
              sessionIdFilter={this.state.sessionIdFilter}
              clicked={this.onClick}
              changed={this.onChanged}
            />
          </div>
        )
        break
      case 'analysis':
        inputSearchContents = (
          <div>
            <Analysis
              appModel={this.props.appModel}
              appName={this.props.appModel.inputAppName}
              clicked={this.onClick}
              changed={this.onChanged}
              userData={this.props.userData}
              transactionData={this.state.transactionData}
              annotationData={this.state.annotationData}
              metadata={this.state.metadata}
            />
          </div>
        )
        break
    }
    const navButtonData: any[] = [
      { label: 'Input', value: 'input' },
      { label: 'Search', value: 'search' },
      { label: 'Results', value: 'results' },
      { label: 'Analysis', value: 'analysis' }
    ]
    const navButtons: any[] = []
    navButtonData.forEach((buttonData: any) => {
      const selectedClass: string = this.state.inputPanelMode === buttonData.value ? 'modeSelected' : ''
      navButtons.push(
        <button
          key={buttonData.value}
          className={`inputPanelButton ${selectedClass}`}
          onClick={() => {
            this.onClick('inputPanelMode', buttonData.value)
          }}
        >
          {buttonData.label}
        </button>
      )
    })

    return (
      <div id="editor">
        <div id="editorContents">
          <div id="inputSearchContainer">
            <div id="inputSearchMenu">{navButtons}</div>
            <div id="inputSearchContents">{inputSearchContents}</div>
          </div>
          <div id="reviewContainer">
            <Transaction appModel={this.props.appModel} data={this.state.transactionData} />
            <Annotation
              appModel={this.props.appModel}
              data={this.state.annotationData}
              userData={this.props.userData}
              clicked={this.onClick}
              changed={this.onChanged}
              metadata={this.state.metadata}
            />
          </div>
        </div>
        <div id="editorStatus">{this.state.statusMessage}</div>
      </div>
    )
  }
}
